import { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Button,
  Table,
  Statistic,
  Tag,
  Space,
  Spin,
  message,
  Tabs,
} from 'antd';
import {
  DownloadOutlined,
  CarOutlined,
  ShoppingOutlined,
  DollarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { apiClient } from '../../lib/api-client';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ============ Types ============

interface TripReportItem {
  id: number;
  companyName: string;
  departureCity: string;
  arrivalCity: string;
  departureRegion: string;
  arrivalRegion: string;
  departureStation: string;
  arrivalStation: string;
  departureTime: string;
  arrivalTime: string;
  busType: string;
  busNumber: string;
  totalSeats: number;
  bookedSeats: number;
  availableSeats: number;
  pricePerSeat: number;
  bookingsCount: number;
  status: string;
}

interface TripSummary {
  totalTrips: number;
  completedTrips: number;
  cancelledTrips: number;
  totalSeats: number;
  bookedSeats: number;
  occupancyRate: number;
}

interface BookingReportItem {
  id: number;
  bookingCode: string;
  companyName: string;
  route: string;
  departureTime: string;
  clientName: string;
  clientPhone: string;
  seatNumbers: string;
  seatsCount: number;
  totalPrice: number;
  isPaid: boolean;
  status: string;
  createdAt: string;
}

interface BookingSummary {
  totalBookings: number;
  confirmedBookings: number;
  cancelledBookings: number;
  completedBookings: number;
  pendingBookings: number;
  paidBookings: number;
  totalRevenue: number;
  paidRevenue: number;
}

interface DailyRevenue {
  date: string;
  revenue: number;
  paid: number;
  bookings: number;
  seats: number;
}

interface RouteRevenue {
  route: string;
  revenue: number;
  bookings: number;
  seats: number;
}

interface RevenueSummary {
  totalRevenue: number;
  paidRevenue: number;
  unpaidRevenue: number;
  totalSeats: number;
  totalCommission: number;
  totalBookings: number;
}

// ============ Helpers ============

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('uz-UZ').format(value) + " so'm";

const formatDate = (date: string) =>
  dayjs(date).format('DD.MM.YYYY HH:mm');

const formatDateShort = (date: string) =>
  dayjs(date).format('DD.MM.YYYY');

const statusColors: Record<string, string> = {
  SCHEDULED: 'blue',
  DEPARTED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
  PENDING: 'gold',
  CONFIRMED: 'cyan',
};

const statusLabels: Record<string, string> = {
  SCHEDULED: 'Rejalashtirilgan',
  DEPARTED: "Jo'nab ketgan",
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
  PENDING: 'Kutilmoqda',
  CONFIRMED: 'Tasdiqlangan',
};

const busTypeLabels: Record<string, string> = {
  STANDARD: 'Standart',
  COMFORT: 'Komfort',
  VIP: 'VIP',
  SLEEPER: 'Sleeper',
};

// ============ Excel Export Functions ============

const styleHeaderRow = (row: ExcelJS.Row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4472C4' },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
  row.height = 24;
};

const styleSummaryCell = (cell: ExcelJS.Cell, isBold = true) => {
  cell.font = { bold: isBold, size: 11 };
  cell.border = {
    top: { style: 'thin' },
    bottom: { style: 'thin' },
    left: { style: 'thin' },
    right: { style: 'thin' },
  };
};

const styleDataRows = (ws: ExcelJS.Worksheet, startRow: number, endRow: number) => {
  for (let i = startRow; i <= endRow; i++) {
    const row = ws.getRow(i);
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        right: { style: 'thin', color: { argb: 'FFD9D9D9' } },
      };
      cell.alignment = { vertical: 'middle' };
    });
    if (i % 2 === 0) {
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF2F7FB' },
        };
      });
    }
  }
};

const exportTripsExcel = async (
  data: TripReportItem[],
  summary: TripSummary,
  dateRange: string,
) => {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Safaro Admin';
  wb.created = new Date();

  const ws = wb.addWorksheet('Reyslar hisoboti');

  // Title
  ws.mergeCells('A1:L1');
  const titleCell = ws.getCell('A1');
  titleCell.value = `Reyslar hisoboti — ${dateRange}`;
  titleCell.font = { bold: true, size: 16, color: { argb: 'FF1A1A2E' } };
  titleCell.alignment = { horizontal: 'center' };
  ws.getRow(1).height = 30;

  // Summary
  ws.getCell('A3').value = 'Jami reyslar:';
  ws.getCell('B3').value = summary.totalTrips;
  ws.getCell('C3').value = 'Yakunlangan:';
  ws.getCell('D3').value = summary.completedTrips;
  ws.getCell('E3').value = 'Bekor qilingan:';
  ws.getCell('F3').value = summary.cancelledTrips;
  ws.getCell('G3').value = "Band o'rinlar:";
  ws.getCell('H3').value = `${summary.bookedSeats} / ${summary.totalSeats} (${summary.occupancyRate}%)`;
  for (const col of ['A', 'C', 'E', 'G']) styleSummaryCell(ws.getCell(`${col}3`), true);
  for (const col of ['B', 'D', 'F', 'H']) styleSummaryCell(ws.getCell(`${col}3`), false);

  // Headers
  const headers = [
    'ID', 'Kompaniya', "Jo'nash shahri", 'Yetib borish shahri',
    "Jo'nash vaqti", 'Yetib borish vaqti', 'Avtobus turi', 'Avtobus raqami',
    "Jami o'rinlar", "Band o'rinlar", "Bo'sh o'rinlar", "Narx (so'm)",
    'Buyurtmalar', 'Holat',
  ];
  const headerRow = ws.addRow(headers);
  styleHeaderRow(headerRow);

  // Data
  const dataStartRow = ws.rowCount + 1;
  for (const t of data) {
    ws.addRow([
      t.id,
      t.companyName,
      t.departureCity,
      t.arrivalCity,
      formatDate(t.departureTime),
      formatDate(t.arrivalTime),
      busTypeLabels[t.busType] || t.busType,
      t.busNumber,
      t.totalSeats,
      t.bookedSeats,
      t.availableSeats,
      t.pricePerSeat,
      t.bookingsCount,
      statusLabels[t.status] || t.status,
    ]);
  }
  styleDataRows(ws, dataStartRow, ws.rowCount);

  // Column widths
  ws.columns = [
    { width: 8 }, { width: 22 }, { width: 18 }, { width: 18 },
    { width: 18 }, { width: 18 }, { width: 14 }, { width: 14 },
    { width: 12 }, { width: 12 }, { width: 12 }, { width: 14 },
    { width: 12 }, { width: 16 },
  ];

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `reyslar_hisoboti_${dayjs().format('YYYY-MM-DD')}.xlsx`);
};

const exportBookingsExcel = async (
  data: BookingReportItem[],
  summary: BookingSummary,
  dateRange: string,
) => {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Safaro Admin';
  wb.created = new Date();

  const ws = wb.addWorksheet('Buyurtmalar hisoboti');

  ws.mergeCells('A1:K1');
  const titleCell = ws.getCell('A1');
  titleCell.value = `Buyurtmalar hisoboti — ${dateRange}`;
  titleCell.font = { bold: true, size: 16, color: { argb: 'FF1A1A2E' } };
  titleCell.alignment = { horizontal: 'center' };
  ws.getRow(1).height = 30;

  // Summary
  ws.getCell('A3').value = 'Jami:';
  ws.getCell('B3').value = summary.totalBookings;
  ws.getCell('C3').value = 'Tasdiqlangan:';
  ws.getCell('D3').value = summary.confirmedBookings;
  ws.getCell('E3').value = 'Bekor qilingan:';
  ws.getCell('F3').value = summary.cancelledBookings;
  ws.getCell('G3').value = "To'langan:";
  ws.getCell('H3').value = summary.paidBookings;
  ws.getCell('I3').value = 'Daromad:';
  ws.getCell('J3').value = formatCurrency(summary.totalRevenue);
  for (const col of ['A', 'C', 'E', 'G', 'I']) styleSummaryCell(ws.getCell(`${col}3`), true);
  for (const col of ['B', 'D', 'F', 'H', 'J']) styleSummaryCell(ws.getCell(`${col}3`), false);

  const headers = [
    'ID', 'Kod', 'Kompaniya', "Yo'nalish", "Jo'nash vaqti",
    'Mijoz ismi', 'Telefon', "O'rindiqlar", "Narx (so'm)",
    "To'langan", 'Holat', 'Sana',
  ];
  const headerRow = ws.addRow(headers);
  styleHeaderRow(headerRow);

  const dataStartRow = ws.rowCount + 1;
  for (const b of data) {
    ws.addRow([
      b.id,
      b.bookingCode,
      b.companyName,
      b.route,
      formatDate(b.departureTime),
      b.clientName,
      b.clientPhone,
      b.seatNumbers,
      b.totalPrice,
      b.isPaid ? 'Ha' : "Yo'q",
      statusLabels[b.status] || b.status,
      formatDateShort(b.createdAt),
    ]);
  }
  styleDataRows(ws, dataStartRow, ws.rowCount);

  ws.columns = [
    { width: 8 }, { width: 12 }, { width: 20 }, { width: 24 },
    { width: 18 }, { width: 20 }, { width: 16 }, { width: 12 },
    { width: 14 }, { width: 12 }, { width: 16 }, { width: 14 },
  ];

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `buyurtmalar_hisoboti_${dayjs().format('YYYY-MM-DD')}.xlsx`);
};

const exportRevenueExcel = async (
  summary: RevenueSummary,
  dailyData: DailyRevenue[],
  routeData: RouteRevenue[],
  dateRange: string,
) => {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Safaro Admin';
  wb.created = new Date();

  // Sheet 1: Summary + Daily
  const ws1 = wb.addWorksheet('Kunlik daromad');

  ws1.mergeCells('A1:F1');
  const titleCell = ws1.getCell('A1');
  titleCell.value = `Daromad hisoboti — ${dateRange}`;
  titleCell.font = { bold: true, size: 16, color: { argb: 'FF1A1A2E' } };
  titleCell.alignment = { horizontal: 'center' };
  ws1.getRow(1).height = 30;

  ws1.getCell('A3').value = 'Umumiy daromad:';
  ws1.getCell('B3').value = formatCurrency(summary.totalRevenue);
  ws1.getCell('C3').value = "To'langan:";
  ws1.getCell('D3').value = formatCurrency(summary.paidRevenue);
  ws1.getCell('E3').value = "To'lanmagan:";
  ws1.getCell('F3').value = formatCurrency(summary.unpaidRevenue);
  ws1.getCell('A4').value = 'Komissiya:';
  ws1.getCell('B4').value = formatCurrency(summary.totalCommission);
  ws1.getCell('C4').value = 'Jami buyurtmalar:';
  ws1.getCell('D4').value = summary.totalBookings;
  ws1.getCell('E4').value = "Jami o'rindiqlar:";
  ws1.getCell('F4').value = summary.totalSeats;
  for (const row of [3, 4]) {
    for (const col of ['A', 'C', 'E']) styleSummaryCell(ws1.getCell(`${col}${row}`), true);
    for (const col of ['B', 'D', 'F']) styleSummaryCell(ws1.getCell(`${col}${row}`), false);
  }

  ws1.addRow([]);
  const dHeaders = ['Sana', "Daromad (so'm)", "To'langan (so'm)", 'Buyurtmalar', "O'rindiqlar"];
  const dHeaderRow = ws1.addRow(dHeaders);
  styleHeaderRow(dHeaderRow);

  const dStartRow = ws1.rowCount + 1;
  for (const d of dailyData) {
    ws1.addRow([
      formatDateShort(d.date),
      d.revenue,
      d.paid,
      d.bookings,
      d.seats,
    ]);
  }
  styleDataRows(ws1, dStartRow, ws1.rowCount);

  // Totals row
  const totalRow = ws1.addRow([
    'JAMI',
    dailyData.reduce((s, d) => s + d.revenue, 0),
    dailyData.reduce((s, d) => s + d.paid, 0),
    dailyData.reduce((s, d) => s + d.bookings, 0),
    dailyData.reduce((s, d) => s + d.seats, 0),
  ]);
  totalRow.eachCell((cell) => {
    cell.font = { bold: true, size: 11 };
    cell.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
  });

  ws1.columns = [{ width: 16 }, { width: 18 }, { width: 18 }, { width: 14 }, { width: 14 }];

  // Sheet 2: By route
  const ws2 = wb.addWorksheet("Yo'nalishlar bo'yicha");

  ws2.mergeCells('A1:D1');
  const t2 = ws2.getCell('A1');
  t2.value = `Yo'nalishlar bo'yicha daromad — ${dateRange}`;
  t2.font = { bold: true, size: 16, color: { argb: 'FF1A1A2E' } };
  t2.alignment = { horizontal: 'center' };
  ws2.getRow(1).height = 30;

  ws2.addRow([]);
  const rHeaders = ["Yo'nalish", "Daromad (so'm)", 'Buyurtmalar', "O'rindiqlar"];
  const rHeaderRow = ws2.addRow(rHeaders);
  styleHeaderRow(rHeaderRow);

  const rStartRow = ws2.rowCount + 1;
  for (const r of routeData) {
    ws2.addRow([r.route, r.revenue, r.bookings, r.seats]);
  }
  styleDataRows(ws2, rStartRow, ws2.rowCount);

  ws2.columns = [{ width: 30 }, { width: 18 }, { width: 14 }, { width: 14 }];

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `daromad_hisoboti_${dayjs().format('YYYY-MM-DD')}.xlsx`);
};

const exportFullReportExcel = async (
  tripsData: TripReportItem[],
  tripsSummary: TripSummary,
  bookingsData: BookingReportItem[],
  bookingsSummary: BookingSummary,
  revenueSummary: RevenueSummary,
  dailyData: DailyRevenue[],
  routeData: RouteRevenue[],
  dateRange: string,
) => {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Safaro Admin';
  wb.created = new Date();

  // Sheet 1: General summary
  const wsSummary = wb.addWorksheet('Umumiy');
  wsSummary.mergeCells('A1:D1');
  const tc = wsSummary.getCell('A1');
  tc.value = `Umumiy hisobot — ${dateRange}`;
  tc.font = { bold: true, size: 16, color: { argb: 'FF1A1A2E' } };
  tc.alignment = { horizontal: 'center' };
  wsSummary.getRow(1).height = 30;

  wsSummary.addRow([]);
  const sHeaders = ["Ko'rsatkich", 'Qiymat'];
  const sHeaderRow = wsSummary.addRow(sHeaders);
  styleHeaderRow(sHeaderRow);

  const summaryRows = [
    ['Jami reyslar', tripsSummary.totalTrips],
    ['Yakunlangan reyslar', tripsSummary.completedTrips],
    ['Bekor qilingan reyslar', tripsSummary.cancelledTrips],
    ["Jami o'rindiqlar", tripsSummary.totalSeats],
    ["Band o'rindiqlar", tripsSummary.bookedSeats],
    ["Band o'rinlar foizi", `${tripsSummary.occupancyRate}%`],
    ['', ''],
    ['Jami buyurtmalar', bookingsSummary.totalBookings],
    ['Tasdiqlangan', bookingsSummary.confirmedBookings],
    ['Yakunlangan', bookingsSummary.completedBookings],
    ['Bekor qilingan', bookingsSummary.cancelledBookings],
    ['Kutilmoqda', bookingsSummary.pendingBookings],
    ["To'langan buyurtmalar", bookingsSummary.paidBookings],
    ['', ''],
    ['Umumiy daromad', formatCurrency(revenueSummary.totalRevenue)],
    ["To'langan daromad", formatCurrency(revenueSummary.paidRevenue)],
    ["To'lanmagan", formatCurrency(revenueSummary.unpaidRevenue)],
    ['Komissiya', formatCurrency(revenueSummary.totalCommission)],
  ];
  const sStartRow = wsSummary.rowCount + 1;
  for (const r of summaryRows) wsSummary.addRow(r);
  styleDataRows(wsSummary, sStartRow, wsSummary.rowCount);
  wsSummary.columns = [{ width: 28 }, { width: 22 }];

  // Sheet 2: Trips
  const wsTrips = wb.addWorksheet('Reyslar');
  const tHeaders = [
    'ID', 'Kompaniya', "Jo'nash", 'Yetib borish', "Jo'nash vaqti",
    'Yetib borish vaqti', 'Turi', 'Raqam', "O'rinlar", 'Band',
    "Bo'sh", "Narx", 'Buyurtmalar', 'Holat',
  ];
  const tHeaderRow = wsTrips.addRow(tHeaders);
  styleHeaderRow(tHeaderRow);
  const tStartRow = wsTrips.rowCount + 1;
  for (const t of tripsData) {
    wsTrips.addRow([
      t.id, t.companyName, t.departureCity, t.arrivalCity,
      formatDate(t.departureTime), formatDate(t.arrivalTime),
      busTypeLabels[t.busType] || t.busType, t.busNumber,
      t.totalSeats, t.bookedSeats, t.availableSeats, t.pricePerSeat,
      t.bookingsCount, statusLabels[t.status] || t.status,
    ]);
  }
  styleDataRows(wsTrips, tStartRow, wsTrips.rowCount);
  wsTrips.columns = [
    { width: 8 }, { width: 20 }, { width: 16 }, { width: 16 },
    { width: 18 }, { width: 18 }, { width: 12 }, { width: 12 },
    { width: 10 }, { width: 10 }, { width: 10 }, { width: 12 },
    { width: 12 }, { width: 16 },
  ];

  // Sheet 3: Bookings
  const wsBookings = wb.addWorksheet('Buyurtmalar');
  const bHeaders = [
    'ID', 'Kod', 'Kompaniya', "Yo'nalish", "Jo'nash",
    'Mijoz', 'Telefon', "O'rinlar", "Narx", "To'langan", 'Holat', 'Sana',
  ];
  const bHeaderRow = wsBookings.addRow(bHeaders);
  styleHeaderRow(bHeaderRow);
  const bStartRow = wsBookings.rowCount + 1;
  for (const b of bookingsData) {
    wsBookings.addRow([
      b.id, b.bookingCode, b.companyName, b.route,
      formatDate(b.departureTime), b.clientName, b.clientPhone,
      b.seatNumbers, b.totalPrice, b.isPaid ? 'Ha' : "Yo'q",
      statusLabels[b.status] || b.status, formatDateShort(b.createdAt),
    ]);
  }
  styleDataRows(wsBookings, bStartRow, wsBookings.rowCount);
  wsBookings.columns = [
    { width: 8 }, { width: 12 }, { width: 20 }, { width: 24 },
    { width: 18 }, { width: 20 }, { width: 16 }, { width: 12 },
    { width: 14 }, { width: 12 }, { width: 16 }, { width: 14 },
  ];

  // Sheet 4: Daily revenue
  const wsDaily = wb.addWorksheet('Kunlik daromad');
  const dHeaders2 = ['Sana', "Daromad", "To'langan", 'Buyurtmalar', "O'rindiqlar"];
  const dHeaderRow2 = wsDaily.addRow(dHeaders2);
  styleHeaderRow(dHeaderRow2);
  const dStartRow2 = wsDaily.rowCount + 1;
  for (const d of dailyData) {
    wsDaily.addRow([formatDateShort(d.date), d.revenue, d.paid, d.bookings, d.seats]);
  }
  styleDataRows(wsDaily, dStartRow2, wsDaily.rowCount);
  wsDaily.columns = [{ width: 16 }, { width: 16 }, { width: 16 }, { width: 14 }, { width: 14 }];

  // Sheet 5: Route revenue
  const wsRoutes = wb.addWorksheet("Yo'nalishlar");
  const rHeaders2 = ["Yo'nalish", "Daromad", 'Buyurtmalar', "O'rindiqlar"];
  const rHeaderRow2 = wsRoutes.addRow(rHeaders2);
  styleHeaderRow(rHeaderRow2);
  const rStartRow2 = wsRoutes.rowCount + 1;
  for (const r of routeData) {
    wsRoutes.addRow([r.route, r.revenue, r.bookings, r.seats]);
  }
  styleDataRows(wsRoutes, rStartRow2, wsRoutes.rowCount);
  wsRoutes.columns = [{ width: 30 }, { width: 16 }, { width: 14 }, { width: 14 }];

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `umumiy_hisobot_${dayjs().format('YYYY-MM-DD')}.xlsx`);
};

// ============ Table Columns ============

const tripColumns: ColumnsType<TripReportItem> = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  { title: 'Kompaniya', dataIndex: 'companyName', key: 'companyName', width: 150 },
  {
    title: "Yo'nalish",
    key: 'route',
    width: 200,
    render: (_, r) => `${r.departureCity} → ${r.arrivalCity}`,
  },
  {
    title: "Jo'nash",
    dataIndex: 'departureTime',
    key: 'departureTime',
    width: 140,
    render: (v: string) => formatDate(v),
  },
  {
    title: "O'rinlar",
    key: 'seats',
    width: 100,
    render: (_, r) => (
      <span>
        <Text strong>{r.bookedSeats}</Text>
        <Text type="secondary"> / {r.totalSeats}</Text>
      </span>
    ),
  },
  {
    title: "Narx",
    dataIndex: 'pricePerSeat',
    key: 'pricePerSeat',
    width: 120,
    render: (v: number) => formatCurrency(v),
  },
  {
    title: 'Holat',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    render: (v: string) => <Tag color={statusColors[v]}>{statusLabels[v] || v}</Tag>,
  },
];

const bookingColumns: ColumnsType<BookingReportItem> = [
  { title: 'Kod', dataIndex: 'bookingCode', key: 'bookingCode', width: 100 },
  { title: "Yo'nalish", dataIndex: 'route', key: 'route', width: 200 },
  {
    title: "Jo'nash",
    dataIndex: 'departureTime',
    key: 'departureTime',
    width: 140,
    render: (v: string) => formatDate(v),
  },
  { title: 'Mijoz', dataIndex: 'clientName', key: 'clientName', width: 150 },
  { title: 'Telefon', dataIndex: 'clientPhone', key: 'clientPhone', width: 130 },
  {
    title: "O'rinlar",
    dataIndex: 'seatNumbers',
    key: 'seatNumbers',
    width: 80,
  },
  {
    title: "Narx",
    dataIndex: 'totalPrice',
    key: 'totalPrice',
    width: 120,
    render: (v: number) => formatCurrency(v),
  },
  {
    title: "To'lov",
    dataIndex: 'isPaid',
    key: 'isPaid',
    width: 80,
    render: (v: boolean) =>
      v ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>Ha</Tag>
      ) : (
        <Tag color="red" icon={<CloseCircleOutlined />}>Yo'q</Tag>
      ),
  },
  {
    title: 'Holat',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    render: (v: string) => <Tag color={statusColors[v]}>{statusLabels[v] || v}</Tag>,
  },
];

const dailyColumns: ColumnsType<DailyRevenue> = [
  {
    title: 'Sana',
    dataIndex: 'date',
    key: 'date',
    width: 120,
    render: (v: string) => formatDateShort(v),
  },
  {
    title: "Daromad",
    dataIndex: 'revenue',
    key: 'revenue',
    width: 140,
    render: (v: number) => formatCurrency(v),
  },
  {
    title: "To'langan",
    dataIndex: 'paid',
    key: 'paid',
    width: 140,
    render: (v: number) => formatCurrency(v),
  },
  { title: 'Buyurtmalar', dataIndex: 'bookings', key: 'bookings', width: 110 },
  { title: "O'rindiqlar", dataIndex: 'seats', key: 'seats', width: 110 },
];

const routeColumns: ColumnsType<RouteRevenue> = [
  { title: "Yo'nalish", dataIndex: 'route', key: 'route', width: 250 },
  {
    title: "Daromad",
    dataIndex: 'revenue',
    key: 'revenue',
    width: 160,
    render: (v: number) => formatCurrency(v),
  },
  { title: 'Buyurtmalar', dataIndex: 'bookings', key: 'bookings', width: 120 },
  { title: "O'rindiqlar", dataIndex: 'seats', key: 'seats', width: 120 },
];

// ============ Component ============

export const BusReports = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  // Report data
  const [tripsData, setTripsData] = useState<TripReportItem[]>([]);
  const [tripsSummary, setTripsSummary] = useState<TripSummary | null>(null);
  const [bookingsData, setBookingsData] = useState<BookingReportItem[]>([]);
  const [bookingsSummary, setBookingsSummary] = useState<BookingSummary | null>(null);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary | null>(null);
  const [dailyData, setDailyData] = useState<DailyRevenue[]>([]);
  const [routeData, setRouteData] = useState<RouteRevenue[]>([]);

  const dateRangeLabel = `${dateRange[0].format('DD.MM.YYYY')} — ${dateRange[1].format('DD.MM.YYYY')}`;

  const fetchReports = async () => {
    setLoading(true);
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');

    try {
      const [tripsRes, bookingsRes, revenueRes] = await Promise.all([
        apiClient.get('/admin/bus/reports/trips', { params: { startDate, endDate } }),
        apiClient.get('/admin/bus/reports/bookings', { params: { startDate, endDate } }),
        apiClient.get('/admin/bus/reports/revenue', { params: { startDate, endDate } }),
      ]);

      setTripsData(tripsRes.data.data);
      setTripsSummary(tripsRes.data.summary);
      setBookingsData(bookingsRes.data.data);
      setBookingsSummary(bookingsRes.data.summary);
      setRevenueSummary(revenueRes.data.summary);
      setDailyData(revenueRes.data.dailyData);
      setRouteData(revenueRes.data.routeData);
    } catch {
      message.error("Hisobotlarni yuklashda xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: string) => {
    if (!tripsSummary || !bookingsSummary || !revenueSummary) {
      message.warning("Avval hisobotni yuklang");
      return;
    }

    setExporting(type);
    try {
      switch (type) {
        case 'trips':
          await exportTripsExcel(tripsData, tripsSummary, dateRangeLabel);
          break;
        case 'bookings':
          await exportBookingsExcel(bookingsData, bookingsSummary, dateRangeLabel);
          break;
        case 'revenue':
          await exportRevenueExcel(revenueSummary, dailyData, routeData, dateRangeLabel);
          break;
        case 'full':
          await exportFullReportExcel(
            tripsData, tripsSummary,
            bookingsData, bookingsSummary,
            revenueSummary, dailyData, routeData,
            dateRangeLabel,
          );
          break;
      }
      message.success('Excel fayl yuklab olindi');
    } catch {
      message.error('Excel faylni yaratishda xatolik');
    } finally {
      setExporting(null);
    }
  };

  const hasData = tripsSummary !== null;

  return (
    <div style={{ padding: '24px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Hisobotlar</Title>
        </Col>
        <Col>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="DD.MM.YYYY"
              style={{ width: 280 }}
            />
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchReports}
              loading={loading}
            >
              Yuklash
            </Button>
            {hasData && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExport('full')}
                loading={exporting === 'full'}
                style={{ background: '#27AE60', borderColor: '#27AE60', color: '#fff' }}
              >
                Umumiy Excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {!hasData && !loading && (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <CarOutlined style={{ fontSize: 48, color: '#bbb', marginBottom: 16 }} />
          <Title level={4} type="secondary">
            Hisobotni ko'rish uchun sanani tanlang va "Yuklash" tugmasini bosing
          </Title>
        </Card>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#888' }}>Hisobotlar yuklanmoqda...</p>
        </div>
      )}

      {hasData && !loading && (
        <>
          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} md={3}>
              <Card size="small">
                <Statistic
                  title="Reyslar"
                  value={tripsSummary.totalTrips}
                  prefix={<CarOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={3}>
              <Card size="small">
                <Statistic
                  title="Band foiz"
                  value={tripsSummary.occupancyRate}
                  suffix="%"
                  valueStyle={{ color: tripsSummary.occupancyRate >= 70 ? '#27AE60' : '#E67E22', fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={3}>
              <Card size="small">
                <Statistic
                  title="Buyurtmalar"
                  value={bookingsSummary!.totalBookings}
                  prefix={<ShoppingOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={3}>
              <Card size="small">
                <Statistic
                  title="To'langan"
                  value={bookingsSummary!.paidBookings}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#27AE60', fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Daromad"
                  value={revenueSummary!.totalRevenue}
                  prefix={<DollarOutlined />}
                  suffix="so'm"
                  valueStyle={{ fontSize: 20 }}
                  formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Komissiya"
                  value={revenueSummary!.totalCommission}
                  valueStyle={{ color: '#9B59B6', fontSize: 20 }}
                  suffix="so'm"
                  formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                />
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Tabs
            items={[
              {
                key: 'trips',
                label: (
                  <span>
                    <CarOutlined /> Reyslar ({tripsSummary.totalTrips})
                  </span>
                ),
                children: (
                  <Card
                    title="Reyslar hisoboti"
                    extra={
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => handleExport('trips')}
                        loading={exporting === 'trips'}
                      >
                        Excel yuklab olish
                      </Button>
                    }
                  >
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Yakunlangan"
                          value={tripsSummary.completedTrips}
                          valueStyle={{ color: '#27AE60', fontSize: 20 }}
                          prefix={<CheckCircleOutlined />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Bekor qilingan"
                          value={tripsSummary.cancelledTrips}
                          valueStyle={{ color: '#E74C3C', fontSize: 20 }}
                          prefix={<CloseCircleOutlined />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Band o'rinlar"
                          value={tripsSummary.bookedSeats}
                          suffix={` / ${tripsSummary.totalSeats}`}
                          valueStyle={{ fontSize: 20 }}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Band foizi"
                          value={tripsSummary.occupancyRate}
                          suffix="%"
                          valueStyle={{ color: tripsSummary.occupancyRate >= 70 ? '#27AE60' : '#E67E22', fontSize: 20 }}
                        />
                      </Col>
                    </Row>
                    <Table
                      columns={tripColumns}
                      dataSource={tripsData}
                      rowKey="id"
                      size="small"
                      scroll={{ x: 900 }}
                      pagination={{ pageSize: 20, showTotal: (total) => `Jami: ${total}` }}
                    />
                  </Card>
                ),
              },
              {
                key: 'bookings',
                label: (
                  <span>
                    <ShoppingOutlined /> Buyurtmalar ({bookingsSummary!.totalBookings})
                  </span>
                ),
                children: (
                  <Card
                    title="Buyurtmalar hisoboti"
                    extra={
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => handleExport('bookings')}
                        loading={exporting === 'bookings'}
                      >
                        Excel yuklab olish
                      </Button>
                    }
                  >
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col xs={12} sm={6} md={3}>
                        <Statistic
                          title="Tasdiqlangan"
                          value={bookingsSummary!.confirmedBookings}
                          valueStyle={{ color: '#3498DB', fontSize: 20 }}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Statistic
                          title="Yakunlangan"
                          value={bookingsSummary!.completedBookings}
                          valueStyle={{ color: '#27AE60', fontSize: 20 }}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Statistic
                          title="Kutilmoqda"
                          value={bookingsSummary!.pendingBookings}
                          prefix={<ClockCircleOutlined />}
                          valueStyle={{ color: '#E67E22', fontSize: 20 }}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Statistic
                          title="Bekor qilingan"
                          value={bookingsSummary!.cancelledBookings}
                          valueStyle={{ color: '#E74C3C', fontSize: 20 }}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title="Umumiy daromad"
                          value={bookingsSummary!.totalRevenue}
                          suffix="so'm"
                          valueStyle={{ fontSize: 20 }}
                          formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title="To'langan"
                          value={bookingsSummary!.paidRevenue}
                          valueStyle={{ color: '#27AE60', fontSize: 20 }}
                          suffix="so'm"
                          formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                        />
                      </Col>
                    </Row>
                    <Table
                      columns={bookingColumns}
                      dataSource={bookingsData}
                      rowKey="id"
                      size="small"
                      scroll={{ x: 1100 }}
                      pagination={{ pageSize: 20, showTotal: (total) => `Jami: ${total}` }}
                    />
                  </Card>
                ),
              },
              {
                key: 'revenue',
                label: (
                  <span>
                    <DollarOutlined /> Daromad
                  </span>
                ),
                children: (
                  <Card
                    title="Daromad hisoboti"
                    extra={
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => handleExport('revenue')}
                        loading={exporting === 'revenue'}
                      >
                        Excel yuklab olish
                      </Button>
                    }
                  >
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title="Umumiy daromad"
                          value={revenueSummary!.totalRevenue}
                          prefix={<DollarOutlined />}
                          suffix="so'm"
                          valueStyle={{ fontSize: 20 }}
                          formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4}>
                        <Statistic
                          title="To'langan"
                          value={revenueSummary!.paidRevenue}
                          valueStyle={{ color: '#27AE60', fontSize: 20 }}
                          suffix="so'm"
                          formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4}>
                        <Statistic
                          title="To'lanmagan"
                          value={revenueSummary!.unpaidRevenue}
                          valueStyle={{ color: '#E74C3C', fontSize: 20 }}
                          suffix="so'm"
                          formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4}>
                        <Statistic
                          title="Komissiya"
                          value={revenueSummary!.totalCommission}
                          valueStyle={{ color: '#9B59B6', fontSize: 20 }}
                          suffix="so'm"
                          formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Statistic title="Buyurtmalar" value={revenueSummary!.totalBookings} valueStyle={{ fontSize: 20 }} />
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Statistic title="O'rindiqlar" value={revenueSummary!.totalSeats} valueStyle={{ fontSize: 20 }} />
                      </Col>
                    </Row>

                    <Title level={5} style={{ marginBottom: 12 }}>Kunlik daromad</Title>
                    <Table
                      columns={dailyColumns}
                      dataSource={dailyData}
                      rowKey="date"
                      size="small"
                      pagination={false}
                      scroll={{ x: 600 }}
                      summary={() => {
                        const totals = dailyData.reduce(
                          (acc, d) => ({
                            revenue: acc.revenue + d.revenue,
                            paid: acc.paid + d.paid,
                            bookings: acc.bookings + d.bookings,
                            seats: acc.seats + d.seats,
                          }),
                          { revenue: 0, paid: 0, bookings: 0, seats: 0 },
                        );
                        return (
                          <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                            <Table.Summary.Cell index={0}>JAMI</Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>{formatCurrency(totals.revenue)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>{formatCurrency(totals.paid)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={3}>{totals.bookings}</Table.Summary.Cell>
                            <Table.Summary.Cell index={4}>{totals.seats}</Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />

                    <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
                      Yo'nalishlar bo'yicha daromad
                    </Title>
                    <Table
                      columns={routeColumns}
                      dataSource={routeData}
                      rowKey="route"
                      size="small"
                      pagination={false}
                      scroll={{ x: 600 }}
                    />
                  </Card>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};
